import { CardActionBar } from "@/components/shared/CardActionBar";
import { LinkedCopiesSection } from "@/components/shared/LinkedCopiesSection";
import { useSelection } from "@/contexts/SelectionContext";
import { GetLinkedItemInfoDocument, ModelEnum, ProjectKind } from "@/generated/graphql";
import { THUMBNAIL } from "@/lib/constants/shared";
import { useEditProjectName } from "@/lib/hooks/projects/useEditProjectName";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import type { ProjectFields } from "@/types/Project";
import type { ViewMode } from "@/types/viewMode";
import { Card, Checkbox, IconButton } from "@ai-teach/ui-web";
import { useLazyQuery } from "@apollo/client/react";
import { type LinkProps, useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import {
  MdCheck,
  MdClose,
  MdDragIndicator,
  MdEdit,
  MdHeadphones,
  MdLink,
  MdVisibilityOff,
} from "react-icons/md";
import { TrashCardActionBar } from "../shared/TrashCardActionBar";

interface ProjectCardProps {
  project: ProjectFields;
  viewMode?: ViewMode;
  url: "trash" | "projectDetail" | "home";
  isSelectionMode: boolean;
  index?: number | null;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  viewMode = "grid",
  url,
  isSelectionMode,
  index = null,
}) => {
  const isList = viewMode === "list";

  const { selectedProjectIds, toggleProject, toggleWarningModal } = useSelection();
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [projectName, setProjectName] = useState<string>(project.name);

  const isSelected = project.id ? selectedProjectIds.includes(project.id) : false;

  const { editProjectName } = useEditProjectName();
  const navigate = useNavigate();

  const handleSaveName = async () => {
    if (projectName.trim() && projectName !== project.name) {
      await editProjectName(project.id, projectName);
    } else {
      setProjectName(project.name);
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setProjectName(project.name);
    setIsEditingName(false);
  };

  useEffect(() => {
    if (!isEditingName) {
      setProjectName(project.name);
    }
  }, [project.name, isEditingName]);

  const isLinkedCopy = project.isLinkedCopy ?? false;
  const originalProjectId = project.originalProjectId ?? null;
  const isHidden = project.isHidden ?? false;
  const hasImage = !!(project.imageId && project.imageUrl);
  // Audio folders sit in the same list as normal projects, so the card has to say which
  // it is before the teacher clicks into it.
  const isAudioFolder = project.kind === ProjectKind.Audio;

  const [getLinkedItemInfoQuery] = useLazyQuery(GetLinkedItemInfoDocument);

  const handleRedirectToOriginal = async () => {
    if (!originalProjectId) return;
    const linkedItem = await getLinkedItemInfoQuery({
      variables: { id: originalProjectId, model: ModelEnum.Project },
    });
    const itemData = linkedItem.data?.linkedItemInfo;
    if (!itemData) return;
    let url: LinkProps["to"];
    saveScrollTarget(`project-${originalProjectId}`, itemData.parentId || "home");
    if (itemData.parentId) {
      url = itemData.isTrash ? "/admin/project/$projectId/trash" : "/admin/project/$projectId";
      navigate({ to: url, params: { projectId: itemData.parentId } });
    } else {
      url = itemData.isTrash ? "/admin/trash" : "/admin";
      navigate({ to: url });
    }
  };

  const handleClickLinked = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!originalProjectId) return;
    toggleWarningModal("You will be redirected to the original item page. Continue?", {
      onCancel: () => toggleWarningModal(false),
      onConfirm: handleRedirectToOriginal,
    });
  };

  // Thumbnail inner content shared between both views
  const thumbnailInner = (
    <>
      {isSelectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <Checkbox
            checked={isSelected}
            onChange={() => {
              if (project.id) toggleProject(project.id, project.isLinkedCopy);
            }}
            size="lg"
          />
        </div>
      )}
      {hasImage ? (
        <img
          src={project.imageUrl ?? "project image url placeholder"}
          alt={project.name}
          className="w-full h-full object-cover"
        />
      ) : isAudioFolder ? (
        <div className="text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center gap-2">
          <MdHeadphones className={isList ? "w-12 h-12" : "w-16 h-16"} aria-hidden="true" />
          {!isList && <p className="text-sm font-medium">Audio</p>}
        </div>
      ) : isList ? (
        <AiFillPicture className="w-12 h-12 text-gray-500 dark:text-gray-400" aria-hidden="true" />
      ) : (
        <div className="text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center gap-2">
          <AiFillPicture className="w-16 h-16" />
          <p className="text-sm font-medium">{THUMBNAIL.projectAspectRatioString}</p>
        </div>
      )}
    </>
  );

  const cardContent = (
    <Card
      hoverable={!isSelectionMode}
      className={`cursor-pointer relative ${
        isLinkedCopy ? "ring-2 ring-secondary-500 shadow-xl" : "shadow-lg"
      } ${isSelected ? "ring-4 ring-primary-500 shadow-xl bg-primary-500/10" : ""} ${
        isHidden ? "opacity-75 border-dashed border-2 border-gray-400 dark:border-gray-600" : ""
      }`}
      onClick={() => {
        if (project.id && isSelectionMode) toggleProject(project.id, project.isLinkedCopy);
      }}
    >
      {/* Action bar */}
      {isSelectionMode ? null : url === "trash" ? (
        <TrashCardActionBar project={project} id={project.id} type="project" />
      ) : (
        <CardActionBar id={project.id || ""} type="project" project={project} />
      )}

      {/* Linked badge */}
      {isLinkedCopy && originalProjectId && (
        <button
          type="button"
          onClick={handleClickLinked}
          className="absolute top-3 left-3 z-20 group/link"
        >
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500/80 backdrop-blur-sm text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200">
            <MdLink className="w-3.5 h-3.5" />
            <span className="text-xs font-semibold">Linked</span>
          </div>
        </button>
      )}

      {/* Hidden badge */}
      {isHidden && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-muted/80 backdrop-blur-sm text-white rounded-full shadow-md">
          <MdVisibilityOff className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">Hidden</span>
        </div>
      )}

      {/* Grid-only: sort badge top-right */}
      {!isList && index !== null && !isHidden && (
        <div className="absolute top-3 right-3 z-20 group/sort">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary-900 backdrop-blur-sm text-white rounded-full shadow-md hover:shadow-lg hover:bg-gray-500 transition-all duration-200 cursor-grab select-none">
            <MdDragIndicator className="w-3.5 h-3.5 opacity-60 group-hover/sort:opacity-100 transition-opacity duration-200" />
            <span className="text-xs font-semibold">{index + 1}</span>
          </div>
          <div className="absolute top-full right-0 mt-2 pointer-events-none opacity-0 group-hover/sort:opacity-100 transition-opacity duration-200 delay-300">
            <div className="relative bg-gray-900 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
              <span className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 rotate-45 block" />
              Drag card to reorder
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className={isList ? "flex items-center gap-5 p-4" : ""}>
        {/* List-only: inline drag handle */}
        {isList && index !== null && (
          <div className="group/sort flex-shrink-0 relative">
            <div className="flex flex-col items-center gap-0.5 px-1.5 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab transition-colors duration-200 select-none rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              <MdDragIndicator className="w-5 h-5" />
              <span className="text-xs font-semibold leading-none">{index + 1}</span>
            </div>
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 pointer-events-none opacity-0 group-hover/sort:opacity-100 transition-opacity duration-200 delay-300 z-30">
              <div className="relative bg-gray-900 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
                <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                Drag card to reorder
              </div>
            </div>
          </div>
        )}
        {/* Thumbnail */}
        {isList ? (
          <div
            className={`flex-shrink-0 w-32 ${THUMBNAIL.projectAspectRatioClass} rounded-lg flex items-center justify-center overflow-hidden relative border border-gray-200 dark:border-gray-800 shadow-sm ${
              hasImage ? "bg-surface" : "bg-gray-200 dark:bg-gray-800"
            } ${isHidden ? "grayscale opacity-60" : ""}`}
          >
            {thumbnailInner}
          </div>
        ) : (
          <div className="relative rounded-2xl overflow-hidden">
            <div
              className={`${THUMBNAIL.projectAspectRatioClass} flex items-center justify-center rounded-t-xl overflow-hidden border-b-2 border-gray-200 dark:border-gray-800 ${
                hasImage ? "bg-surface" : "bg-gray-200 dark:bg-gray-800"
              } ${isHidden ? "grayscale opacity-60" : ""}`}
            >
              {thumbnailInner}
            </div>
          </div>
        )}

        {/* List-only: vertical divider */}
        {isList && (
          <div className="h-24 w-px bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
        )}

        {/* Content */}
        <div
          className={isList ? "flex-1 min-w-0" : "p-4 pt-5 z-50 overflow-hidden"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="group/title flex items-center gap-2 mb-1">
            <div className="flex-1">
              <input
                type="text"
                value={projectName}
                readOnly={!isEditingName}
                className={`w-full truncate bg-transparent border-none outline-none card-title ${
                  isEditingName ? "ring-2 ring-blue-500 rounded px-2 py-1" : "px-0"
                }`}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isEditingName) setIsEditingName(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveName();
                  else if (e.key === "Escape") handleCancelEdit();
                  e.stopPropagation();
                }}
                onBlur={handleSaveName}
              />
            </div>

            {!isEditingName ? (
              <IconButton
                aria-label="Edit project name"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditingName(true);
                }}
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover/title:opacity-100 transition-opacity duration-200 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-100 flex-shrink-0"
              >
                <MdEdit className="w-4 h-4" aria-hidden="true" />
              </IconButton>
            ) : (
              <div className="flex gap-1 flex-shrink-0">
                <IconButton
                  aria-label="Save"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSaveName();
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-success-500 hover:bg-success-700/20"
                >
                  <MdCheck className="w-4 h-4" />
                </IconButton>
                <IconButton
                  aria-label="Cancel"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-error-500 hover:bg-error-700/20"
                >
                  <MdClose className="w-4 h-4" />
                </IconButton>
              </div>
            )}
          </div>

          <div className={isList ? "mt-1 flex items-center gap-2" : "flex items-center gap-2"}>
            {isAudioFolder && (
              <span className="badge-secondary inline-flex items-center gap-1">
                <MdHeadphones className="w-3.5 h-3.5" aria-hidden="true" />
                Audio
              </span>
            )}
            <span className="badge-secondary">
              {project.itemCount}{" "}
              {isAudioFolder
                ? project.itemCount === 1
                  ? "track"
                  : "tracks"
                : project.itemCount === 0 || project.itemCount === 1
                  ? "item"
                  : "items"}
            </span>
          </div>

          <LinkedCopiesSection
            linkedCopies={project.linkedCopies}
            type="Project"
            originalItemId={project.id}
          />
        </div>
      </div>
    </Card>
  );

  const handleCardClick = () => {
    if (project.id) {
      saveScrollTarget(`project-${project.id}`, project.parentId || "home");
    }
    navigate({ to: "/admin/project/$projectId", params: { projectId: project.id } });
  };

  if (isSelectionMode || url === "trash") {
    return <div id={`project-${project.id}`}>{cardContent}</div>;
  }

  return (
    <div id={`project-${project.id}`}>
      <Card onClick={handleCardClick} className="cursor-pointer">
        {cardContent}
      </Card>
    </div>
  );
};
